// Guest persistence: everything stays in this browser's localStorage.
import {
  AUDIENCES,
  DEFAULT_PREFERENCES,
  LENSES,
  LIMITS,
  type Preferences,
  type SavedStory,
} from "../../shared/model.ts";
import { ROLES } from "../../shared/roles.ts";
import { normalize, type Basket } from "./basket.ts";

const BASKET_KEY = "nsp.basket.v1";
const PREFS_KEY = "nsp.prefs.v1";

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or disabled (private mode). The basket still works in memory.
  }
}

function isSavedStory(v: unknown): v is SavedStory {
  const s = v as SavedStory;
  return (
    !!s &&
    typeof s.storyId === "string" &&
    LIMITS.storyIdPattern.test(s.storyId) &&
    typeof s.savedAt === "number" &&
    typeof s.order === "number" &&
    typeof s.isRead === "boolean"
  );
}

export function loadGuestBasket(): Basket {
  const raw = read<unknown[]>(BASKET_KEY);
  if (!Array.isArray(raw)) return [];
  return normalize(
    raw.filter(isSavedStory).map((s) => ({
      ...s,
      readAt: typeof s.readAt === "number" ? s.readAt : null,
      readingProgress: typeof s.readingProgress === "number" ? s.readingProgress : null,
    })),
  );
}

export function saveGuestBasket(basket: Basket): void {
  write(BASKET_KEY, basket);
}

export function clearGuestBasket(): void {
  try {
    localStorage.removeItem(BASKET_KEY);
  } catch {
    /* ignore */
  }
}

export function loadGuestPrefs(): Preferences {
  const p = read<Partial<Preferences>>(PREFS_KEY) ?? {};
  return {
    ...DEFAULT_PREFERENCES,
    audience: AUDIENCES.includes(p.audience as never) ? p.audience! : DEFAULT_PREFERENCES.audience,
    lens: LENSES.includes(p.lens as never) ? p.lens! : DEFAULT_PREFERENCES.lens,
    role: p.role === "all" || ROLES.includes(p.role as never) ? p.role! : DEFAULT_PREFERENCES.role,
  };
}

export function saveGuestPrefs(p: Preferences): void {
  write(PREFS_KEY, p);
}
